import React, { useEffect, useMemo, useState } from 'react';
import { apiFetch, parseJsonSafe } from '../utils/api';

const fmtDate = (d) => {
  if (!d) return '-';
  const s = String(d).slice(0, 10);
  const [y, m, day] = s.split('-');
  return (y && m && day) ? `${day}/${m}/${y}` : s;
};

const fmtPrecio = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

const ServicesPage = () => {
  const token = localStorage.getItem('token');
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({ id: null, titulo: '', descripcion: '', tipo: 'unico', recurrente_cada: '', precio: '' });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('catalogo');
  const [contracted, setContracted] = useState([]);
  const [loadingContracted, setLoadingContracted] = useState(false);
  const [contractedError, setContractedError] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [contractedPage, setContractedPage] = useState(1);
  const contractedPageSize = 20;

  const handleUnauthorized = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    window.location.href = '/login';
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/services', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'Error cargando servicios');
      setServices(Array.isArray(data) ? data : []);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchContracted = async () => {
    setLoadingContracted(true);
    setContractedError('');
    try {
      const res = await apiFetch('/api/services/contracted', { headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error((data && data.message) || 'Error cargando servicios contratados');
      setContracted(Array.isArray(data) ? data : []);
      setContractedPage(1);
    } catch (err) {
      setContracted([]);
      setContractedError(err.message);
    } finally {
      setLoadingContracted(false);
    }
  };

  useEffect(() => {
    fetchServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (view === 'contratados') fetchContracted();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const filteredContracted = useMemo(() => {
    const q = clientFilter.trim().toLowerCase();
    return contracted
      .filter((s) => !q || (s.client_name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.client_name || '').localeCompare(b.client_name || '', 'es', { sensitivity: 'base' }));
  }, [contracted, clientFilter]);

  const totalContractedPages = Math.max(Math.ceil(filteredContracted.length / contractedPageSize), 1);
  const pagedContracted = useMemo(() => {
    const start = (contractedPage - 1) * contractedPageSize;
    return filteredContracted.slice(start, start + contractedPageSize);
  }, [filteredContracted, contractedPage]);

  const resetForm = () => setForm({ id: null, titulo: '', descripcion: '', tipo: 'unico', recurrente_cada: '', precio: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.titulo) return alert('El título es obligatorio');
    if (!form.tipo) return alert('Selecciona tipo');
    if (form.tipo === 'recurrente' && !form.recurrente_cada.trim()) return alert('Indica cada cuánto es recurrente');
    if (form.precio === '') return alert('Precio obligatorio');
    const payload = {
      titulo: form.titulo,
      descripcion: form.descripcion,
      tipo: form.tipo,
      recurrente_cada: form.tipo === 'recurrente' ? form.recurrente_cada : '',
      precio: form.precio
    };
    const isEdit = Boolean(form.id);
    const url = isEdit ? `/api/services/${form.id}` : '/api/services';
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      resetForm();
      fetchServices();
      alert(isEdit ? 'Servicio actualizado' : 'Servicio creado');
    } catch (err) {
      alert(err.message);
    }
  };

  const edit = (svc) => {
    setForm({
      id: svc.id,
      titulo: svc.titulo,
      descripcion: svc.descripcion || '',
      tipo: svc.tipo,
      recurrente_cada: svc.recurrente_cada || '',
      precio: svc.precio
    });
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar servicio?')) return;
    try {
      const res = await apiFetch(`/api/services/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      const data = await parseJsonSafe(res);
      if (res.status === 401) return handleUnauthorized();
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      fetchServices();
    } catch (err) {
      alert(err.message);
    }
  };

  const total = useMemo(() => services.length, [services]);

  return (
    <div className="small" style={{ fontSize: '0.9rem' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-0">Servicios</h2>
          <div className="text-muted">Gestiona servicios únicos o recurrentes.</div>
        </div>
        {view === 'catalogo' && <div className="text-muted">Total: {total}</div>}
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${view === 'catalogo' ? 'active' : ''}`}
            onClick={() => setView('catalogo')}
          >
            Catálogo
          </button>
        </li>
        <li className="nav-item">
          <button
            type="button"
            className={`nav-link ${view === 'contratados' ? 'active' : ''}`}
            onClick={() => setView('contratados')}
          >
            Contratados
          </button>
        </li>
      </ul>

      {view === 'contratados' ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label className="form-label small mb-1">Filtrar por cliente</label>
                <input
                  className="form-control"
                  value={clientFilter}
                  onChange={(e) => { setClientFilter(e.target.value); setContractedPage(1); }}
                  placeholder="Nombre del cliente"
                />
              </div>
            </div>
            {contractedError && <div className="alert alert-danger">{contractedError}</div>}
            {loadingContracted ? (
              <p className="text-muted mb-0">Cargando servicios contratados...</p>
            ) : filteredContracted.length ? (
              <>
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th>Cliente</th>
                        <th>Servicio</th>
                        <th>Periodicidad</th>
                        <th>Próxima renovación</th>
                        <th className="text-end">Precio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedContracted.map((s, i) => (
                        <tr key={`${s.factura_id}-${s.client_id}-${i}`}>
                          <td>{s.client_name || '-'}</td>
                          <td>{s.concepto || '-'}</td>
                          <td>{s.periodicidad || '-'}</td>
                          <td>{fmtDate(s.proxima_renovacion)}</td>
                          <td className="text-end">{fmtPrecio(s.precio)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredContracted.length > contractedPageSize && (
                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted small">Página {contractedPage} de {totalContractedPages} (total {filteredContracted.length})</div>
                    <div className="d-flex gap-2">
                      <button className="btn btn-sm btn-outline-secondary" disabled={contractedPage <= 1} onClick={() => setContractedPage((p) => p - 1)}>Anterior</button>
                      <button className="btn btn-sm btn-outline-secondary" disabled={contractedPage >= totalContractedPages} onClick={() => setContractedPage((p) => p + 1)}>Siguiente</button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-muted mb-0">Sin servicios contratados. Se generan a partir de las facturas recurrentes de los clientes.</p>
            )}
          </div>
        </div>
      ) : (
      <>
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <form className="row g-2" onSubmit={submit}>
            <div className="col-md-4">
              <label className="form-label small mb-1">Título *</label>
              <input
                className="form-control"
                value={form.titulo}
                onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))}
                placeholder="Ej: Mantenimiento web"
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label small mb-1">Descripción</label>
              <input
                className="form-control"
                value={form.descripcion}
                onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
                placeholder="Descripción opcional del servicio"
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Tipo *</label>
              <select
                className="form-select"
                value={form.tipo}
                onChange={(e) => {
                  const nextTipo = e.target.value;
                  setForm((p) => ({
                    ...p,
                    tipo: nextTipo,
                    recurrente_cada: nextTipo === 'recurrente' ? p.recurrente_cada : ''
                  }));
                }}
                required
              >
                <option value="unico">Único</option>
                <option value="recurrente">Recurrente</option>
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label small mb-1">Recurrente cada</label>
              <input
                className="form-control"
                value={form.recurrente_cada}
                onChange={(e) => setForm((p) => ({ ...p, recurrente_cada: e.target.value }))}
                placeholder="Ej: mensual, cada 3 meses"
                disabled={form.tipo !== 'recurrente'}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small mb-1">Precio *</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control"
                value={form.precio}
                onChange={(e) => setForm((p) => ({ ...p, precio: e.target.value }))}
                placeholder="0.00"
                required
              />
            </div>
            <div className="col-12 d-flex gap-2 justify-content-end">
              <button className="btn btn-primary" type="submit">
                {form.id ? 'Actualizar' : 'Añadir'}
              </button>
              {form.id && (
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="card shadow-sm">
        <div className="card-body">
          {loading ? (
            <p>Cargando servicios...</p>
          ) : services.length ? (
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Descripción</th>
                    <th>Tipo</th>
                    <th>Recurrente</th>
                    <th>Precio</th>
                    <th>Creado</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((s) => (
                    <tr key={s.id}>
                      <td>{s.titulo}</td>
                      <td className="text-muted">{s.descripcion || '-'}</td>
                      <td>{s.tipo === 'recurrente' ? 'Recurrente' : 'Único'}</td>
                      <td>{s.tipo === 'recurrente' ? (s.recurrente_cada || '-') : '-'}</td>
                      <td>{Number(s.precio).toFixed(2)} €</td>
                      <td>{s.created_at ? new Date(s.created_at).toLocaleDateString() : '-'}</td>
                      <td className="text-end">
                        <button className="btn btn-sm btn-outline-secondary me-2" onClick={() => edit(s)}>Editar</button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => remove(s.id)}>Eliminar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-muted mb-0">No hay servicios.</p>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default ServicesPage;
